import { useState, useRef, useEffect, useCallback } from "react";
import { analytics } from "../firebase"; // 1. Import analytics
import { logEvent } from "firebase/analytics"; // 2. Import logEvent

// --- Component Imports ---
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { Button } from "./button";
import { Input } from "./Input";
import { ScrollArea } from "./scroll-area";
import { Send, Bot, User } from "lucide-react";

export default function AIchat({ isOpen, onClose, language }) {
  const [messages, setMessages] = useState(() => [
    {
      id: 'initial-ai',
      type: 'ai',
      content:
        language === 'hi'
          ? 'नमस्ते! मैं आपका Buddy In Hills AI सहायक हूं। उत्तराखंड के होटल, झीलों, बाइक रेंटल, टैक्सी और ट्रेकिंग के बारे में पूछें।'
          : "Hello! I'm your Buddy In Hills AI assistant. Ask me about Uttarakhand hotels, lakes, bike rentals, taxis, and trekking.",
      timestamp: new Date(),
    },
  ]);

  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to the bottom of the chat when messages change or dialog opens
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen, isTyping]); // Added isTyping to re-scroll when typing indicator appears/disappears

  const quickQuestions =
    language === 'hi'
      ? [
          "नैनीताल में सबसे अच्छे होटल कौन से हैं?",
          "भीमताल में बाइक रेंटल की कीमत क्या है?",
          "नैना पीक ट्रेक कैसे करें?",
          "स्थानीय टैक्सी की दरें क्या हैं?",
        ]
      : [
          "Best hotels in Nainital?",
          "Bike rental prices in Bhimtal?",
          "How to trek to Naina Peak?",
          "Local taxi rates?",
        ];

  const getAIResponse = useCallback((userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    if (language === 'hi') {
      if (lowerMessage.includes('होटल') || lowerMessage.includes('hotel')) {
        return "नैनीताल के टॉप होटल:\n1. माउंटेन व्यू रिज़ॉर्ट - ₹4,500/रात\n2. लेक साइड इन - ₹3,200/रात\n3. हिल स्टेशन लॉज - ₹2,800/रात\n\nसभी में WiFi, पार्किंग और झील का दृश्य उपलब्ध है।";
      }
      if (lowerMessage.includes('बाइक') || lowerMessage.includes('bike')) {
        return "नैनीताल में बाइक रेंटल:\n• स्कूटर: ₹400-500/दिन\n• मोटरसाइकिल: ₹800-1000/दिन\n• इलेक्ट्रिक बाइक: ₹600/दिन\n\nड्राइविंग लाइसेंस और ID की आवश्यकता।";
      }
      if (lowerMessage.includes('ट्रेक') || lowerMessage.includes('trek')) {
        return "लोकप्रिय ट्रेकिंग रूट:\n1. नैना पीक - 6km, मध्यम कठिनाई\n2. टिफिन टॉप - 4km, आसान\n3. स्नो व्यू पॉइंट - 2.5km, आसान\n\nगाइड: ₹800/दिन, ट्रेकिंग गियर उपलब्ध।";
      }
      if (lowerMessage.includes('टैक्सी') || lowerMessage.includes('taxi')) {
        return "स्थानीय टैक्सी दरें:\n• शहर के अंदर: ₹15-20/km\n• नैनीताल से भीमताल: ₹800\n• नैनीताल से नौकुचियाताल: ₹1,200\n• पूरे दिन: ₹2,500-3,000";
      }
      if (lowerMessage.includes('झील') || lowerMessage.includes('lake')) {
        return "उत्तराखंड में प्रसिद्ध झीलें:\n1. नैनी झील - नैनीताल\n2. भीमताल झील - भीमताल\n3. सात ताल - सात ताल\n4. नौकुचियाताल - नौकुचियाताल\n\nआप नाव की सवारी और प्राकृतिक सुंदरता का आनंद ले सकते हैं।";
      }
      return "मैं उत्तराखंड के होटल, ट्रेकिंग, बाइक रेंटल, टैक्सी सेवाओं और झीलों के बारे में जानकारी दे सकता हूं। कृपया अधिक विशिष्ट प्रश्न पूछें।";
    } else {
      if (lowerMessage.includes('hotel')) {
        return "Top Nainital hotels:\n1. Mountain View Resort - ₹4,500/night\n2. Lake Side Inn - ₹3,200/night\n3. Hill Station Lodge - ₹2,800/night\n\nAll include WiFi, parking, and lake views.";
      }
      if (lowerMessage.includes('bike')) {
        return "Nainital bike rentals:\n• Scooter: ₹400-500/day\n• Motorcycle: ₹800-1000/day\n• Electric bike: ₹600/day\n\nDriving license and ID required.";
      }
      if (lowerMessage.includes('trek')) {
        return "Popular trekking routes:\n1. Naina Peak - 6km, moderate difficulty\n2. Tiffin Top - 4km, easy\n3. Snow View Point - 2.5km, easy\n\nGuide: ₹800/day, trekking gear available.";
      }
      if (lowerMessage.includes('taxi')) {
        return "Local taxi rates:\n• Within city: ₹15-20/km\n• Nainital to Bhimtal: ₹800\n• Nainital to Naukuchiatal: ₹1,200\n• Full day: ₹2,500-3,000";
      }
      if (lowerMessage.includes('lake')) {
        return "Famous lakes in Uttarakhand:\n1. Naini Lake - Nainital\n2. Bhimtal Lake - Bhimtal\n3. Sattal - Sattal\n4. Naukuchiatal - Naukuchiatal\n\nYou can enjoy boating and scenic beauty.";
      }
      return "I can help with information about Uttarakhand hotels, trekking, bike rentals, taxi services, and lakes. Please ask more specific questions.";
    }
  }, [language]); // Depend on language

  const handleSendMessage = useCallback(() => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    // 3. Log the custom event when a message is sent
    if (analytics) {
        logEvent(analytics, 'custom_ask_ai', {
            question_text: userMessage.content // Send the full user question
        });
    }

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsTyping(true);

    // Simulate AI response delay
    setTimeout(() => {
      const aiMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: getAIResponse(userMessage.content),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500);
  }, [inputMessage, getAIResponse]); // Depend on inputMessage and getAIResponse

  const handleQuickQuestion = useCallback((question) => {
    setInputMessage(question);
    // Use a small delay to allow inputMessage to update before sending
    setTimeout(() => handleSendMessage(), 100);
  }, [handleSendMessage]); // Depend on handleSendMessage

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="
           flex-col
          h-[min(90vh,700px)] max-h-[90vh]
          w-[95vw] sm:w-[90vw] md:w-[70vw] lg:w-[600px]
          max-w-5xl
          p-4
          overflow-hidden // Ensure content inside respects flex layout
        "
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center space-x-2">
            <Bot className="w-5 h-5 text-primary" />
            <span>Buddy In Hills AI Assistant</span>
          </DialogTitle>
        </DialogHeader>

        {/* Main content area within the dialog */}
        <div className="flex-1 flex flex-col space-y-4 overflow-hidden">
          {/* Messages Scroll Area */}
          <ScrollArea className="flex-1 pr-2">
            <div className="space-y-4 pb-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`flex items-start space-x-2 max-w-[80%] sm:max-w-[70%] lg:max-w-[60%] ${
                      message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        message.type === 'user' ? 'bg-primary' : 'bg-secondary'
                      }`}
                    >
                      {message.type === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div
                      className={`rounded-lg p-3 text-sm ${
                        message.type === 'user'
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-50'
                      }`}
                    >
                      <p className="whitespace-pre-line">{message.content}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="flex items-start space-x-2">
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-gray-100 rounded-lg p-3 dark:bg-gray-700">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0.1s' }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0.2s' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} /> {/* Element to scroll into view */}
            </div>
          </ScrollArea>

          {/* Quick Questions - show only if there's only the initial AI message */}
          {messages.length === 1 && (
            <div className="space-y-2 p-2 rounded-lg bg-white dark:bg-gray-800 border-t dark:border-gray-700">
              <p className="text-sm text-muted-foreground dark:text-gray-400">
                {language === 'hi' ? 'त्वरित प्रश्न:' : 'Quick questions:'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {quickQuestions.map((question, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="text-left justify-start h-auto p-2 text-xs truncate"
                    onClick={() => handleQuickQuestion(question)}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="pt-2"> {/* Added pt-2 to provide spacing */}
            <div className="flex items-center space-x-2">
              <Input
                placeholder={
                  language === 'hi'
                    ? 'यहाँ अपना प्रश्न लिखें...'
                    : 'Type your question here...'
                }
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                className="flex-1 rounded-md"
                disabled={isTyping} // Disable input while AI is typing
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isTyping}
                className="bg-primary hover:bg-primary/90 text-white rounded-md transition-transform active:scale-95"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}